import type { City, Service, ServiceSlug } from '@/types'

import { keyLocalities } from '@/constants/cities'

const otherServicesAnswer = (city: City, current: string, others: string): string => (
    `Along with ${current}, we provide ${others} across ${city.name}, so one partner can look after your office IT.`
)

export const services: Array<Service> = [
    {
        slug: 'laptop-rental',
        index: '01',
        name: "Laptop on rent",
        shortName: "Laptop rental",
        pageTitle: "Laptop on rent in Delhi NCR for businesses",
        heading: "Laptop on rent for businesses across Delhi NCR",
        metaDescription: "Rent business laptops in Delhi NCR for new hires, projects and remote teams. Configured, delivered and supported by Rent-O-Tech. Get a free quote today.",
        summary: "Business laptops for new hires, projects and hybrid teams, configured and supported for the whole rental term.",
        capsule: "Rent-O-Tech rents business laptops to companies across Delhi NCR on flexible terms, from a few machines for a new office to large batches for whole teams and projects. Every laptop is configured to your requirement, tested before delivery and supported for the full rental, with repairs and replacements handled by us.",
        image: {
            src: '/images/laptop-rental.jpg',
            alt: "Professional working on a business laptop at a bright office desk"
        },
        highlights: [
            "Core i3 to Core i7 and Ryzen",
            "Bulk orders set up identically",
            "Repairs and replacements included"
        ],
        featuresTitle: "What is included with every laptop rental?",
        features: [
            {
                title: "Configured for your work",
                description: "Processor, memory, storage and operating system matched to the software your team actually uses."
            },
            {
                title: "Delivered to your office",
                description: "Laptops arrive tested and ready to use at your office anywhere in Delhi NCR."
            },
            {
                title: "Repairs and replacements",
                description: "If a laptop develops a fault, we repair or replace it so your team keeps working."
            },
            {
                title: "Scales with your headcount",
                description: "Add laptops for new joiners and return them when a project or contract ends."
            },
            {
                title: "GST invoice",
                description: "Clear monthly billing with a GST invoice for your accounts team."
            },
            {
                title: "Data wiped on return",
                description: "Returned laptops are wiped before they go back into our fleet."
            }
        ],
        optionsTitle: "Which laptop configuration does your team need?",
        optionsIntro: "Most teams fit one of these profiles. Tell us the software you use and we will confirm the exact model.",
        optionsNote: "Models and availability change with stock, so we confirm exact specifications in your quote.",
        options: [
            {
                name: "Office essentials",
                spec: "Core i3 or i5 · 8 GB RAM · 256 GB SSD",
                bestFor: "Email, browser apps, Office, Tally, ERP and CRM"
            },
            {
                name: "Performance",
                spec: "Core i5 or i7 · 16 GB RAM · 512 GB SSD",
                bestFor: "Developers, analysts and heavy multitasking"
            },
            {
                name: "Workstation",
                spec: "Core i7 or Ryzen 7 · 32 GB RAM · dedicated graphics",
                bestFor: "Design, video editing, CAD and data work"
            },
            {
                name: "MacBook",
                spec: "MacBook Air or MacBook Pro, on request",
                bestFor: "Teams that work on macOS"
            }
        ],
        useCasesTitle: "Who rents laptops from Rent-O-Tech?",
        useCases: [
            {
                title: "New hires and growing teams",
                description: "Give every joiner a ready-to-use laptop on day one without buying hardware upfront."
            },
            {
                title: "Projects and contract staff",
                description: "Equip auditors, consultants and temporary staff for exactly as long as the work lasts."
            },
            {
                title: "Hybrid and remote teams",
                description: "Standard laptops your employees can take home, with support when something breaks."
            },
            {
                title: "Training, exams and events",
                description: "Identical laptops for classrooms, assessments, conferences and roadshows."
            }
        ],
        faqs: [
            {
                question: "How many laptops can I rent at once?",
                answer: "You can rent a few laptops for a new office or large batches for whole teams and training programmes. For bulk orders we set up every laptop identically, so each user starts with the same software and settings."
            },
            {
                question: "Can I choose the laptop configuration?",
                answer: "Yes. Share the software your team uses and we recommend a processor, memory and storage combination that fits, from everyday office laptops to high-performance workstations."
            },
            {
                question: "Do rented laptops come with an operating system and software?",
                answer: "Laptops are delivered with the operating system installed and ready to use. We can also install the applications your team needs, using your licences or as agreed in your quote."
            },
            {
                question: "Can I extend the rental or return laptops early?",
                answer: "You can extend a rental for as long as you need the laptops. Early returns and changes in quantity follow the terms agreed in your quote, so share your expected timeline upfront."
            },
            {
                question: "What happens if a rented laptop stops working?",
                answer: "Report the issue and we repair or replace the laptop so work does not stop. Normal faults are covered for the full rental term, while physical damage is handled as per the rental agreement."
            }
        ],
        city: {
            heading: (city) => `Laptop on rent in ${city.name} for businesses`,
            metaDescription: (city) => `Laptop on rent in ${city.name} for startups, MSMEs and corporates. Business laptops configured, delivered and supported by Rent-O-Tech. Get a free quote.`,
            capsule: (city) => `Rent-O-Tech delivers business laptops on rent to companies across ${city.name}, including ${keyLocalities(city)}. Share how many laptops you need, the configuration and the rental period, and we configure, deliver and support them at your office for the whole term.`,
            faqs: (city) => [
                {
                    question: `Do you deliver laptops on rent in ${city.name}?`,
                    answer: `Yes. We deliver, set up and support rented laptops for businesses across ${city.name}, including ${keyLocalities(city)}.`
                },
                {
                    question: `How quickly can you deliver rented laptops in ${city.name}?`,
                    answer: `Delivery is planned around your start date. Share your requirement as early as you can, and we confirm availability and a delivery date for your ${city.name} office with your quote.`
                },
                {
                    question: `What else does Rent-O-Tech offer in ${city.name}?`,
                    answer: otherServicesAnswer(city, "laptop rental", "desktop rental, IT hardware AMC and refurbished laptop sales")
                }
            ]
        }
    },
    {
        slug: 'desktop-rental',
        index: '02',
        name: "Desktop on rent",
        shortName: "Desktop rental",
        pageTitle: "Desktop on rent in Delhi NCR for offices",
        heading: "Desktop on rent for offices across Delhi NCR",
        metaDescription: "Rent office desktops and workstations in Delhi NCR with monitor, keyboard and mouse. Installed and maintained by Rent-O-Tech. Get a free quote today.",
        summary: "Complete desktop sets for call centres, back offices, training rooms and project floors, installed at your desks.",
        capsule: "Rent-O-Tech rents office desktops and workstations to businesses across Delhi NCR as complete sets with monitor, keyboard and mouse, installed at your desks. It is a fast way to set up a call centre, back office, training room or project floor without buying hardware, with maintenance and replacements included for the full rental term.",
        image: {
            src: '/images/desktop-rental.jpg',
            alt: "Team member with a headset working on an office desktop computer"
        },
        highlights: [
            "Complete sets with monitor",
            "Installed at your desks",
            "Workstations on request"
        ],
        featuresTitle: "What is included with every desktop rental?",
        features: [
            {
                title: "Complete desktop sets",
                description: "CPU, monitor, keyboard and mouse delivered together, ready to plug in and use."
            },
            {
                title: "Installed at your office",
                description: "We set up and connect desktops at your desks so your team can start straight away."
            },
            {
                title: "Built for shift work",
                description: "Reliable configurations for call centres and back offices that run long hours."
            },
            {
                title: "Maintenance included",
                description: "Faults are fixed, or the system is replaced, for the full rental term."
            },
            {
                title: "Flexible seat count",
                description: "Add desktops for new shifts and projects, and return them when you no longer need them."
            },
            {
                title: "GST invoice",
                description: "Clear monthly billing with a GST invoice for your accounts team."
            }
        ],
        optionsTitle: "Which desktop setup fits your office?",
        optionsIntro: "Pick a starting point and we will match it to your software, seating and network.",
        optionsNote: "Models and availability change with stock, so we confirm exact specifications in your quote.",
        options: [
            {
                name: "Office desktop",
                spec: "Core i3 or i5 · 8 GB RAM · 256 GB SSD · 19 to 22 inch monitor",
                bestFor: "Call centres, back office, data entry and billing counters"
            },
            {
                name: "Performance desktop",
                spec: "Core i5 or i7 · 16 GB RAM · 512 GB SSD · 22 to 24 inch monitor",
                bestFor: "Accounts, development and multitasking"
            },
            {
                name: "Workstation",
                spec: "Core i7 or Xeon · 32 GB RAM or more · dedicated graphics",
                bestFor: "CAD, rendering, video and data analysis"
            }
        ],
        useCasesTitle: "Who rents desktops from Rent-O-Tech?",
        useCases: [
            {
                title: "Call centres and BPOs",
                description: "Rows of identical desktops installed and ready for every shift."
            },
            {
                title: "Back offices and operations",
                description: "Desktops for accounts, billing, data entry and support teams."
            },
            {
                title: "Training rooms and exam centres",
                description: "Labs set up identically for classes, assessments and recruitment drives."
            },
            {
                title: "Project floors",
                description: "Extra seats for a client project, audit or seasonal peak, returned when it ends."
            }
        ],
        faqs: [
            {
                question: "Do rented desktops come with a monitor?",
                answer: "Yes. Desktops are rented as complete sets with monitor, keyboard and mouse. Larger or dual monitors can be added on request."
            },
            {
                question: "Can you set up desktops for a call centre or training room?",
                answer: "Yes. We deliver and install desktops in bulk, configured identically, so a call centre, training room or exam centre is ready on the day you need it."
            },
            {
                question: "Do you rent workstations for design and engineering work?",
                answer: "Yes. We rent workstation-class desktops with more memory and dedicated graphics for CAD, design, video and data work. Share your software and we will suggest a configuration."
            },
            {
                question: "What happens if a rented desktop develops a fault?",
                answer: "Tell us and we repair or replace it. Maintenance is included for the full rental term, so routine faults do not turn into repair bills."
            }
        ],
        city: {
            heading: (city) => `Desktop on rent in ${city.name} for offices`,
            metaDescription: (city) => `Desktop on rent in ${city.name} for offices, call centres and training rooms. Complete sets installed and maintained by Rent-O-Tech. Get a free quote.`,
            capsule: (city) => `Rent-O-Tech supplies office desktops on rent to businesses across ${city.name}, including ${keyLocalities(city)}. Desktops arrive as complete sets with monitor, keyboard and mouse, are installed at your desks and stay maintained for the full rental term.`,
            faqs: (city) => [
                {
                    question: `Do you install rented desktops in ${city.name}?`,
                    answer: `Yes. We deliver and install desktop sets at offices across ${city.name}, including ${keyLocalities(city)}.`
                },
                {
                    question: `How quickly can desktops be installed in ${city.name}?`,
                    answer: `Installation is planned around your start date. Share your requirement as early as you can, and we confirm availability and an installation date for your ${city.name} office with your quote.`
                },
                {
                    question: `What else does Rent-O-Tech offer in ${city.name}?`,
                    answer: otherServicesAnswer(city, "desktop rental", "laptop rental, IT hardware AMC and refurbished laptop sales")
                }
            ]
        }
    },
    {
        slug: 'amc-services',
        index: '03',
        name: "AMC services",
        shortName: "AMC services",
        pageTitle: "IT hardware AMC services in Delhi NCR",
        heading: "IT hardware AMC services for businesses in Delhi NCR",
        metaDescription: "Annual maintenance contracts for laptops, desktops and printers in Delhi NCR. Preventive visits, breakdown support and repairs from Rent-O-Tech.",
        summary: "Annual maintenance contracts that keep the laptops, desktops and printers you own working, for a fixed yearly fee.",
        capsule: "An IT hardware AMC (annual maintenance contract) from Rent-O-Tech keeps the laptops, desktops, printers and other IT equipment your business owns in working order for a fixed yearly fee. It covers preventive maintenance, breakdown support and repairs across Delhi NCR, so your team is not left waiting when something fails.",
        image: {
            src: '/images/amc-services.jpg',
            alt: "Employee working on a well-maintained office desktop computer"
        },
        highlights: [
            "Comprehensive or non-comprehensive",
            "Preventive maintenance visits",
            "Breakdown support"
        ],
        featuresTitle: "What does an IT hardware AMC cover?",
        features: [
            {
                title: "Preventive maintenance",
                description: "Scheduled visits to clean, check and tune your systems before small issues become breakdowns."
            },
            {
                title: "Breakdown support",
                description: "Call us when a device fails and we diagnose and fix it, on site wherever possible."
            },
            {
                title: "Your choice of cover",
                description: "Comprehensive AMC includes replacement parts. Non-comprehensive AMC covers service and labour only."
            },
            {
                title: "One contract for all hardware",
                description: "Laptops, desktops, printers and other office IT hardware under a single agreement."
            },
            {
                title: "Service records",
                description: "A record of every visit and repair, so you always know the condition of your hardware."
            },
            {
                title: "Fixed yearly cost",
                description: "A predictable annual fee instead of unplanned repair bills."
            }
        ],
        optionsTitle: "Comprehensive or non-comprehensive AMC?",
        optionsIntro: "Both include preventive maintenance and breakdown support. The difference is who pays for replacement parts.",
        optionsNote: "The yearly fee depends on the number, type, age and condition of your devices, so we review your hardware before quoting.",
        options: [
            {
                name: "Comprehensive AMC",
                spec: "Service, labour and replacement parts covered",
                bestFor: "Offices that want one fixed cost and no surprise bills"
            },
            {
                name: "Non-comprehensive AMC",
                spec: "Service and labour covered, parts billed when needed",
                bestFor: "Newer hardware with a lower chance of part failures"
            }
        ],
        useCasesTitle: "Who needs an IT hardware AMC?",
        useCases: [
            {
                title: "Offices without in-house IT",
                description: "Get a dependable technical team without hiring one."
            },
            {
                title: "Hardware out of warranty",
                description: "Keep older laptops and desktops productive once the manufacturer warranty ends."
            },
            {
                title: "Growing MSMEs",
                description: "One contract that grows with the number of devices in your office."
            },
            {
                title: "Businesses with several offices",
                description: "Consistent support for branches spread across Delhi NCR."
            }
        ],
        faqs: [
            {
                question: "What is the difference between comprehensive and non-comprehensive AMC?",
                answer: "A comprehensive AMC covers service, labour and replacement parts for a fixed yearly fee. A non-comprehensive AMC covers service and labour, and parts are billed separately when needed."
            },
            {
                question: "Which devices can be covered under an AMC?",
                answer: "Laptops, desktops, printers and other office IT hardware from all major brands. Share a list of your devices and we will include them in one contract."
            },
            {
                question: "How is the AMC price decided?",
                answer: "The yearly fee depends on how many devices you have, their type, age and condition, and whether parts are included. We review your hardware and send a clear quote."
            },
            {
                question: "Can I take an AMC for laptops that are out of warranty?",
                answer: "Yes. An AMC is most useful once the manufacturer warranty has ended. We inspect older devices before including them in the contract."
            }
        ],
        city: {
            heading: (city) => `IT hardware AMC services in ${city.name}`,
            metaDescription: (city) => `IT hardware AMC in ${city.name} for laptops, desktops and printers. Preventive maintenance, breakdown support and repairs from Rent-O-Tech. Get a quote.`,
            capsule: (city) => `Rent-O-Tech provides annual maintenance contracts for laptops, desktops, printers and other IT hardware to businesses across ${city.name}, including ${keyLocalities(city)}. We handle preventive maintenance, breakdown calls and repairs, so your team keeps working.`,
            faqs: (city) => [
                {
                    question: `Do you provide IT hardware AMC in ${city.name}?`,
                    answer: `Yes. We cover laptops, desktops, printers and other IT hardware for businesses across ${city.name}, including ${keyLocalities(city)}.`
                },
                {
                    question: `How do breakdown calls work for offices in ${city.name}?`,
                    answer: "Call or email us with the device and the problem. We diagnose it, fix what we can on site and arrange repairs for the rest, as agreed in your contract."
                },
                {
                    question: `What else does Rent-O-Tech offer in ${city.name}?`,
                    answer: otherServicesAnswer(city, "AMC services", "laptop rental, desktop rental and refurbished laptop sales")
                }
            ]
        }
    },
    {
        slug: 'refurbished-laptops',
        index: '04',
        name: "Refurbished laptops",
        shortName: "Refurbished laptops",
        pageTitle: "Refurbished business laptops in Delhi NCR",
        heading: "Refurbished business laptops for teams in Delhi NCR",
        metaDescription: "Buy tested refurbished business laptops from Dell, HP and Lenovo in Delhi NCR. Inspected, cleaned and sold with warranty by Rent-O-Tech. Get a quote.",
        summary: "Inspected, business-grade laptops from Dell, HP and Lenovo for much less than the price of new ones.",
        capsule: "Rent-O-Tech sells refurbished business laptops from brands such as Dell, HP and Lenovo to companies, startups and professionals across Delhi NCR. Each laptop is inspected, repaired where needed, cleaned and tested before sale, so you get business-grade build quality for much less than a new machine, with warranty and a GST invoice.",
        image: {
            src: '/images/refurbished-laptops.jpg',
            alt: "Refurbished business laptop on a clean desk next to a notebook and pen"
        },
        highlights: [
            "Dell, HP and Lenovo business models",
            "Inspected and tested",
            "Warranty and GST invoice"
        ],
        featuresTitle: "What makes our refurbished laptops business-ready?",
        features: [
            {
                title: "Business-grade models",
                description: "Latitude, EliteBook and ThinkPad class laptops built for years of daily office use."
            },
            {
                title: "Inspected and tested",
                description: "Battery, display, keyboard, ports, storage and performance checked before sale."
            },
            {
                title: "Clean installation",
                description: "Storage wiped and the operating system freshly installed, ready for a new user."
            },
            {
                title: "Warranty included",
                description: "Every laptop is sold with warranty, with the terms shared in your quote."
            },
            {
                title: "Bulk orders",
                description: "Buy for a whole team and get every laptop set up the same way."
            },
            {
                title: "GST invoice",
                description: "Every sale is billed with a GST invoice for your business."
            }
        ],
        optionsTitle: "Which refurbished laptop suits your team?",
        optionsIntro: "Refurbished stock changes from week to week. These are the profiles most businesses ask for.",
        optionsNote: "Share your budget and quantity and we will send the best matching models in stock.",
        options: [
            {
                name: "Everyday business",
                spec: "Core i5 · 8 GB RAM · 256 GB SSD",
                bestFor: "Office work, accounts and sales teams"
            },
            {
                name: "Performance",
                spec: "Core i5 or i7 · 16 GB RAM · 512 GB SSD",
                bestFor: "Developers, analysts and managers"
            },
            {
                name: "Thin and light",
                spec: "Core i5 or i7 · 16 GB RAM · 13 to 14 inch",
                bestFor: "Founders and teams who travel"
            }
        ],
        useCasesTitle: "Who buys refurbished laptops from Rent-O-Tech?",
        useCases: [
            {
                title: "Startups on a budget",
                description: "Own reliable laptops for your team while keeping cash for growth."
            },
            {
                title: "MSMEs replacing old machines",
                description: "Upgrade an ageing set of laptops for much less than buying new."
            },
            {
                title: "Teams that need laptops long term",
                description: "When you will use laptops for several years, owning refurbished often costs less than renting."
            },
            {
                title: "Schools and training institutes",
                description: "Affordable, dependable laptops for computer labs and classrooms."
            }
        ],
        faqs: [
            {
                question: "Are refurbished laptops reliable for business use?",
                answer: "Business laptops are built for years of daily use, and ours are inspected, repaired where needed and tested before sale. For most office work, a refurbished business laptop performs like a new one at a much lower price."
            },
            {
                question: "Do refurbished laptops come with warranty?",
                answer: "Yes. Every refurbished laptop is sold with warranty, and the exact terms for each model are shared with your quote."
            },
            {
                question: "Can I buy refurbished laptops in bulk for my team?",
                answer: "Yes. We supply refurbished laptops in bulk to startups and businesses, set up identically so your team is ready on day one."
            },
            {
                question: "Should I rent laptops or buy refurbished?",
                answer: "Rent when you need flexibility, a short term or no upfront cost. Buy refurbished when you want to own laptops for several years at the lowest price. We can compare both options for your team."
            }
        ],
        city: {
            heading: (city) => `Refurbished business laptops in ${city.name}`,
            metaDescription: (city) => `Refurbished business laptops in ${city.name} from Dell, HP and Lenovo. Tested, cleaned and sold with warranty by Rent-O-Tech. Get a quote.`,
            capsule: (city) => `Rent-O-Tech sells refurbished business laptops to companies, startups and institutes across ${city.name}, including ${keyLocalities(city)}. Every laptop is inspected, cleaned and tested before sale, and comes with warranty and a GST invoice.`,
            faqs: (city) => [
                {
                    question: `Do you deliver refurbished laptops in ${city.name}?`,
                    answer: `Yes. We deliver refurbished business laptops to companies and institutes across ${city.name}, including ${keyLocalities(city)}.`
                },
                {
                    question: `Can I buy refurbished laptops in bulk in ${city.name}?`,
                    answer: `Yes. We supply refurbished laptops in bulk to businesses across ${city.name}, set up identically and billed with a GST invoice.`
                },
                {
                    question: `What else does Rent-O-Tech offer in ${city.name}?`,
                    answer: otherServicesAnswer(city, "refurbished laptop sales", "laptop rental, desktop rental and IT hardware AMC")
                }
            ]
        }
    }
]

export const getService = (slug: string): Service | undefined => services.find((service) => service.slug === slug)

export const isServiceSlug = (value: string): value is ServiceSlug => services.some((service) => service.slug === value)
